import React, { useState } from 'react';
import { Save } from 'lucide-react';
import { Button } from '../common/Button/Button';
import { Input } from '../common/Input/Input';
import { AppSettings } from '../../types';

export const GeneralSettings: React.FC = () => {
  const [settings, setSettings] = useState<AppSettings>({
    siteName: 'Telangana Government CMS',
    siteDescription: 'Official Content Management System for Telangana Government',
    adminEmail: 'admin@telangana.gov.in',
    maxFileSize: 5,
    allowedFileTypes: ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx'],
    autoPublish: false,
    emailNotifications: true,
    maintenanceMode: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      // Save settings logic here
      console.log('Settings saved:', settings);
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (key: keyof AppSettings, value: any) => {
    setSettings({ ...settings, [key]: value });
  };

  return (
    <div className="space-y-6">
      {/* Site Information */}
      <div className="bg-white shadow-sm border rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Site Information</h3>
        <div className="space-y-4">
          <Input
            label="Site Name"
            value={settings.siteName}
            onChange={(e) => updateSetting('siteName', e.target.value)}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Site Description
            </label>
            <textarea
              rows={3}
              value={settings.siteDescription}
              onChange={(e) => updateSetting('siteDescription', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <Input
            label="Admin Email"
            type="email"
            value={settings.adminEmail}
            onChange={(e) => updateSetting('adminEmail', e.target.value)}
          />
        </div>
      </div>

      {/* File Upload Settings */}
      <div className="bg-white shadow-sm border rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">File Upload Settings</h3>
        <div className="space-y-4">
          <Input
            label="Max File Size (MB)"
            type="number"
            value={settings.maxFileSize.toString()}
            onChange={(e) => updateSetting('maxFileSize', parseInt(e.target.value))}
          />
          <Input
            label="Allowed File Types"
            value={settings.allowedFileTypes.join(', ')}
            onChange={(e) => updateSetting('allowedFileTypes', e.target.value.split(', '))}
            helperText="Separate file extensions with commas"
          />
        </div>
      </div>

      {/* Content Settings */}
      <div className="bg-white shadow-sm border rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Content Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={settings.autoPublish}
              onChange={(e) => updateSetting('autoPublish', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label className="ml-2 text-sm text-gray-900">
              Auto-publish content after creation
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={settings.emailNotifications}
              onChange={(e) => updateSetting('emailNotifications', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label className="ml-2 text-sm text-gray-900">
              Send email notifications for new content
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={settings.maintenanceMode}
              onChange={(e) => updateSetting('maintenanceMode', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label className="ml-2 text-sm text-gray-900">
              Enable maintenance mode
            </label>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          loading={loading}
          leftIcon={<Save className="w-4 h-4" />}
        >
          Save Settings
        </Button>
      </div>
    </div>
  );
};